import { MessageAttachment } from "@aichat/types/index";
import { cn } from "@aichat/ui/lib/utils";
import { Typography } from "../../../typography";
import { File } from "lucide-react";

type AttachmentComponentProps = {
  attachments: MessageAttachment[];
  isCustomer?: boolean;
  type: "widget-chat" | "portal-chat";
  showAvatar: boolean;
};

const imageExts = [".png", ".jpg", ".jpeg", ".gif"];

function AttachmentComponent(props: AttachmentComponentProps) {
  const { attachments, isCustomer, type } = props;
  const imageFiles = attachments.filter(
    (att) =>
      att.type === "image" || imageExts.some((ext) => att.url.includes(ext)),
  );
  const documentFiles = attachments.filter(
    (att) =>
      !(att.type === "image" || imageExts.some((ext) => att.url.includes(ext))),
  );
  return (
    <div
      className={cn("w-full flex", {
        "justify-end":
          (isCustomer && type === "widget-chat") ||
          (!isCustomer && type === "portal-chat"),
      })}
    >
      <div className="max-w-[372px]">
        <div className="flex gap-2 items-center">
          <div className="flex flex-col gap-4">
            {/* Image Files - 4x4 Grid */}
            {imageFiles.length > 0 && (
              <div className="grid grid-cols-3 gap-2 w-fit">
                {imageFiles.map((attachment, index) => (
                  <a
                    href={attachment.url}
                    target="_blank"
                    key={`image-${attachment.url}-${index}`}
                  >
                    <ImageThumbnail attachment={attachment} />
                  </a>
                ))}
              </div>
            )}

            {/* Document Files (including videos) - Vertical Stack */}
            {documentFiles.map((attachment, index) => (
              <a
                href={attachment.url}
                target="_blank"
                key={`doc-${attachment.url}-${index}`}
              >
                <DocumentAttachment
                  attachment={attachment}
                  key={`doc-${attachment.url}-${index}`}
                />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageThumbnail(props: { attachment: MessageAttachment }) {
  const { attachment } = props;

  return (
    <div className="relative w-20 h-20 rounded-lg overflow-hidden border bg-neutral-100 border-neutral-200 shadow-sm cursor-pointer hover:opacity-80 transition-opacity">
      <img
        src={attachment.url || "/placeholder.svg?height=80&width=80"}
        alt={attachment.name || "Image thumbnail"}
        className="w-full h-full object-contain"
      />
    </div>
  );
}

function DocumentAttachment(props: { attachment: MessageAttachment }) {
  const { attachment } = props;

  const fileType = attachment.url.split(".").at(-1);

  return (
    <div className="rounded-xl p-2 hover:bg-gray-750 transition-colors cursor-pointer border border-neutral-200 bg-neutral-50">
      <div className="flex items-start gap-3">
        {/* File Icon */}
        <div
          className={cn(
            "h-8 aspect-square rounded-lg p-2 flex items-center justify-center bg-neutral-500/20",
          )}
        >
          <File className="h-4 w-4 text-gray-400" />
        </div>

        {/* File Info */}
        <div className="flex-1 max-w-[200px]">
          <Typography
            variant="subtleMedium"
            className="truncate"
            title={attachment.name}
          >
            {attachment.name || "Unknown File"}
          </Typography>
          {fileType && (
            <Typography
              variant="tiny"
              className="text-muted-foreground uppercase"
            >
              {fileType}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttachmentComponent;
